"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoveRight, ChevronRight, X, MoveLeft } from "lucide-react";
import Image from "next/image";
import { useIsMobile } from "@hooks/use-mobile";
import styles from "./CarGallery.module.scss";

interface CarGalleryProps {
  isOpen: boolean;
  galleryImages: string[];
  className?: string;
  onClose: () => void;
  preventScroll: boolean;
}

export default function CarGallery({
  isOpen,
  galleryImages,
  className,
  onClose,
  preventScroll = true
}: CarGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen && preventScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, preventScroll]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && galleryImages.length > 1) {
      nextImage();
    }
    if (isRightSwipe && galleryImages.length > 1) {
      prevImage();
    }
  };

  if (!isOpen || !galleryImages || galleryImages.length === 0 || !mounted) {
    return null;
  }

  const modalContent = (
    <div 
      ref={overlayRef} 
      className={`${styles.modalOverlay} ${className || ''}`}
      onClick={handleOverlayClick}
    >
      <div className={styles.carGallery}>
        <X size={30} onClick={onClose} className={styles.carGalleryClose} aria-label="Close gallery" />
        { galleryImages.length > 1 && !isMobile && <MoveLeft size={30} className={styles.galleryIcon} onClick={prevImage} aria-label="Previous image" /> }
         
          <div 
            className={styles.carGallerySection}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
          >
            <Image
              src={galleryImages?.[currentImageIndex]}
              alt={`Car view ${currentImageIndex + 1}`}
              className={styles.carGalleryImage}
              fill
              loading='lazy'
            />
          </div>
          { galleryImages.length > 1 && !isMobile && <MoveRight size={30} className={styles.galleryIcon} onClick={nextImage} aria-label="Next image" /> }
          
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
